import React, { useMemo } from "react";
import { Badge, Stack, Text, Title } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { SPACER_PADDED } from "@flights/constants";
import { getTimelineLegData, TimelineLeg } from "./TimelineLeg.js";
import { TimelineStop } from "./TimelineStop.js";
import Frame from "../../Frame/index.js";
import styles from "./TimelineSegment.module.css";
import useSearchCabinClass from "../../../../hooks/useSearchCabinClass.js";
import { formattingUtils } from "@flights/web-api-utils-universal";
import useProfiler from "../../../../hooks/useProfiler.js";
import { FlightTimelineUI } from "../FlightTimeline.UI/index.js";
import { useTimelineLegController } from "./useTimelineLeg.controller.js";
import { trackWithDefaultStage } from "@flights/et-universal";
export const TimelineSegment = ({ segment, segmentIndex, noMargin, hideCabinClassMismatch, hideHeading = false, headerBadgeProps }) => {
    const { isPb } = useProfiler();
    const { getTimelineLegFormattedData } = useTimelineLegController();
    return (React.createElement(Frame, { className: `${styles.root} ${noMargin ? styles.rootNoMargin : ""}`, "data-testid": `timeline_segment_${segmentIndex}` },
        !hideHeading ? (React.createElement(Header, { segment: segment, hideCabinClassMismatch: hideCabinClassMismatch, badgeProps: headerBadgeProps })) : undefined,
        React.createElement("div", { className: styles.legsWrapper }, !isPb() && trackWithDefaultStage("flights_funnel_web_bui_timeline", 1) ? (React.createElement(FlightTimelineUI, { items: segment.timeline
                .map((data, idx) => getItemData({
                idx,
                data,
                getTimelineLegFormattedData,
                prevTimeLineData: segment.timeline[idx - 1],
                nextTimeLineData: segment.timeline[idx + 1],
                isLastOne: idx === segment.timeline.length - 1
            }))
                .flat() })) : (segment.timeline.map((item, index) => (React.createElement(Item, { data: item, isLastOne: index === segment.timeline.length - 1, key: index, prevTimeLineData: segment.timeline[index - 1], nextTimeLineData: segment.timeline[index + 1], idx: index })))))));
};
const Header = ({ segment, hideCabinClassMismatch = false, badgeProps }) => {
    const i18n = useI18n();
    const { arrivalAirport, stops, durationMinutes } = segment;
    const { searchCabinClass } = useSearchCabinClass();
    const isCabinClassMismatch = useMemo(() => {
        if (hideCabinClassMismatch) {
            return false;
        }
        return segment.legs.some((l) => l.cabinClass !== searchCabinClass);
    }, [segment, searchCabinClass, hideCabinClassMismatch]);
    const stopsText = useMemo(() => {
        if (stops === 0) {
            return i18n.trans(t("flights_filter_stops_none"));
        }
        else {
            return i18n.trans(t("flights_route_num_stops", { num_exception: stops, variables: { num_stops: stops } }));
        }
    }, [i18n, stops]);
    const durationText = useMemo(() => {
        return formattingUtils.flightDuration(durationMinutes, i18n);
    }, [i18n, durationMinutes]);
    const durationTextAria = useMemo(() => {
        return formattingUtils.flightDurationAria(durationMinutes, i18n);
    }, [i18n, durationMinutes]);
    return (React.createElement(Frame, { mb: 4 },
        React.createElement(Stack, { gap: 1, direction: "column" },
            badgeProps && (React.createElement(Stack.Item, null,
                React.createElement(Badge, { ...badgeProps }))),
            React.createElement(Title, { variant: "strong_1", attributes: { "data-testid": "timeline_segment_title" }, title: i18n.trans(t("flights_details_route", {
                    variables: {
                        city_name: arrivalAirport.cityName || arrivalAirport.name
                    }
                })), titleTagName: "h2" })),
        React.createElement(Text, { variant: "body_2", className: styles.summary, attributes: {
                "data-testid": "timeline_segment_flight_duration",
                "aria-label": `${stopsText} ${durationTextAria} ${isCabinClassMismatch ? i18n.trans(t("flights_fd_cabin_mismatch")) : ""}`
            } },
            stopsText,
            SPACER_PADDED,
            durationText,
            isCabinClassMismatch ? ` ${i18n.trans(t("flights_fd_cabin_mismatch"))}` : null)));
};
const Item = ({ data, isLastOne, prevTimeLineData, nextTimeLineData, idx }) => {
    if (data.type === "FLIGHT") {
        return React.createElement(TimelineLeg, { idx: idx, leg: data, shouldHideAnythingBeneath: isLastOne });
    }
    else if (data.type === "STOP") {
        return React.createElement(TimelineStop, { stop: data, prevLegData: prevTimeLineData, nextLegData: nextTimeLineData });
    }
    return null;
};
function getItemData(props) {
    const { data, idx, prevTimeLineData, nextTimeLineData, getTimelineLegFormattedData } = props;
    return data.type === "FLIGHT"
        ? getTimelineLegData({
            idx,
            leg: data,
            getTimelineLegFormattedData
        })
        : data.type === "STOP"
            ? [
                {
                    type: "content",
                    content: (React.createElement("div", null,
                        React.createElement(TimelineStop, { noMargin: true, noPadding: true, stop: data, prevLegData: prevTimeLineData, nextLegData: nextTimeLineData })))
                }
            ]
            : [];
}
